import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { FreeMatchTierData } from 'src/app/core/api/player/schemas/tiers';
import { CurrencyPricingData } from 'src/app/core/api/player/schemas/room';
import { WaitingOpponentsData } from 'src/app/core/api/player/schemas/waiting-opponents';
import { CurrencyAmountDisplay } from 'src/app/shared/utils/currency/currency-format';
import { PaymentService } from 'src/app/features/payments/services/payment.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { FreeMatchPlayEventData } from 'src/app/core/services/tiers/models/payment-events';
import { Countdown } from 'src/app/shared/utils/app.utils';
import { ThemePipe } from '../../../../core/pipes/theme.pipe';
import { IconComponent } from '../../../../ui/icon/icon/icon.component';
import { SplicedBackgroundComponent } from '../../../../ui/surface/spliced-background/spliced-background.component';
import { WagerEntryLineComponent } from '../wager-entry-line/wager-entry-line.component';
import { WagerPrizeComponent } from '../wager-prize/wager-prize.component';

// An operator-granted free match, rendered as a single-width tile among the duel tiers. It
// is deliberately not a BaseWagerCardDirective: that directive fuses the payment latch with
// insufficient-balance dimming and tier lock state, none of which apply to a grant — the
// tile is always playable and goes inert only while a payment intent is in flight.
//
// Visually the paid wager card is the precedent: card chrome, title, prize and entry line
// use its tokens and its shared children, so the two tiles read as one family. The free
// tile differs only in what it adds — the struck fee with the "Free" word, the "N free"
// pill, the expiry row and the button's face colour.
@Component({
  selector: 'app-free-match-card',
  templateUrl: './free-match-card.component.html',
  styleUrls: ['./free-match-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgStyle,
    ThemePipe,
    IconComponent,
    SplicedBackgroundComponent,
    WagerPrizeComponent,
    WagerEntryLineComponent,
  ],
})
export class FreeMatchCardComponent {
  readonly grant = input.required<FreeMatchTierData>();
  // Shared tick from the tier list, so every tile's countdown advances on one interval.
  readonly now = input.required<Date>();
  // The standard-duel waiting map; a grant plays into its tier's normal queue, so an
  // opponent waiting on that tier is an opponent waiting for this tile too.
  readonly waitingOpponents = input<WaitingOpponentsData | undefined>();

  readonly clickEvent = output<FreeMatchPlayEventData>();

  private readonly themeService = inject(ThemeService);
  private readonly paymentService = inject(PaymentService);
  private readonly translocoService = inject(TranslocoService);

  protected readonly splicedBg = this.themeService.splicedBackgroundPlacement('wager-card');
  protected readonly splicedPlayBg =
    this.themeService.splicedBackgroundPlacement('wager-play-button');

  readonly isDisabled = computed(() => this.paymentService.paymentInFlight());
  // Must match the wager card's lookup so a waiting opponent lights both tiles alike.
  readonly hasWaitingOpponent = computed(
    () => this.waitingOpponents()?.waitingOpponents[this.grant().tierId] ?? false,
  );
  // A single remaining play needs no counter; the button already says it is free.
  readonly showCountPill = computed(() => this.grant().remaining > 1);

  readonly countLabel = computed(() =>
    this.translocoService.translate('components.wager-options.free-count', {
      count: this.grant().remaining,
    }),
  );

  // Minute-floored, so it only changes when the rendered label would; the 1 s tick reaches
  // this cheap computation but not the translation below, which memoises on the string.
  private readonly coarseTimeLeft = computed(() => {
    const remainingSeconds = Math.floor((this.grant().expiresAt - this.now().getTime()) / 1000);
    return Countdown.asCoarseTimeLeft(remainingSeconds);
  });

  readonly expiresIn = computed(() =>
    this.translocoService.translate('components.timeout-label.expires-in', {
      timeRemaining: this.coarseTimeLeft(),
    }),
  );

  private readonly primaryAmounts = computed(() =>
    this.themeService.displayCurrency() === 'match-and-user'
      ? this.grant().amounts.matchCurrency
      : this.grant().amounts.userCurrency,
  );

  readonly primaryPrizeAmount = computed(() =>
    this.formatCurrency(this.primaryAmounts(), 'reward'),
  );
  readonly secondaryPrizeAmount = computed(() =>
    this.formatCurrency(this.grant().amounts.userCurrency, 'reward'),
  );
  readonly primaryEntryAmount = computed(() =>
    this.formatCurrency(this.primaryAmounts(), 'entryFee'),
  );
  readonly secondaryEntryAmount = computed(() =>
    this.formatCurrency(this.grant().amounts.userCurrency, 'entryFee'),
  );

  readonly ariaLabel = computed(
    () => `${this.title} — win ${this.primaryPrizeAmount().text}, ${this.freeLabel}`,
  );

  readonly cardStyle: [string, string][] = [
    ['background', 'card-wager-unlocked'],
    ['border', 'card-wager-unlocked'],
    ['drop-shadow', 'card-wager'],
    ['border-radius', 'card-wager'],
  ];

  // Like the countdown pill, a flat capsule: no border or shadow tokens.
  readonly countPillStyles: [string, string][] = [
    ['background', 'pill-free-match'],
    ['border-radius', 'pill-free-match'],
  ];

  readonly countPillLabelStyles: [string, string][] = [
    ['type', 'pill-free-match-label'],
    ['text-color', 'pill-free-match-label'],
  ];

  readonly titleStyles: [string, string][] = [
    ['type', 'card-wager-unlocked-label-win'],
    ['text-color', 'card-wager-unlocked-label-win'],
  ];

  readonly prizeShadowStyles: [string, string][] = [
    ['type', 'card-wager-unlocked-label-prize'],
    ['text-color', 'card-wager-unlocked-label-prize'],
  ];

  readonly prizeStyles = computed((): [string, string][] => {
    const key = 'card-wager-unlocked-label-prize';
    const textColorType = this.themeService.hasWagerCardGradientTextColor()
      ? 'text-color-gradient'
      : 'text-color';
    return [
      ['type', key],
      ['text-outer-border', key],
      [textColorType, key],
    ];
  });

  readonly enterStyle: [string, string][] = [
    ['type', 'card-wager-unlocked-label-enter'],
    ['text-color', 'card-wager-unlocked-label-enter'],
  ];

  // The struck fee is the paid card's fee, dimmed by the entry line — not its own token.
  readonly feeStyle: [string, string][] = [
    ['type', 'card-wager-unlocked-label-fee'],
    ['text-color', 'card-wager-unlocked-label-fee'],
  ];

  readonly freeStyle: [string, string][] = [
    ['type', 'card-free-match-label-free'],
    ['text-color', 'card-free-match-label-free'],
  ];

  readonly playButtonStyles: [string, string][] = [
    ['background-gradient', 'button-free-match'],
    ['border-radius', 'button-free-match'],
    ['height', 'button-free-match'],
    ['border', 'button-free-match'],
    ['drop-and-inner-shadow', 'button-free-match'],
  ];

  readonly playButtonLabelStyles: [string, string][] = [
    ['type', 'button-free-match-label'],
    ['text-color', 'button-free-match-label'],
  ];

  readonly expiryLabelStyles: [string, string][] = [
    ['type', 'card-free-match-label-expiry'],
    ['text-color', 'card-free-match-label-expiry'],
  ];

  readonly playerWaitingColor = '--panel-wager-tier-list-player-waiting-fill';
  readonly topPadding = 'var(--card-wager-padding-top)';
  readonly bottomPadding = 'var(--card-wager-padding-bottom)';
  readonly leftPadding = 'var(--card-wager-padding-left)';
  readonly rightPadding = 'var(--card-wager-padding-right)';

  readonly title = this.translocoService.translate('components.wager-options.title');
  readonly entryLabel = this.translocoService.translate('components.wager-options.entry');
  readonly freeLabel = this.translocoService.translate('components.wager-options.free');
  readonly playLabel = this.translocoService.translate('components.wager-options.free-play');

  onClick(): void {
    if (this.isDisabled()) {
      return;
    }
    this.clickEvent.emit({ type: 'freeMatch', grant: this.grant() });
  }

  private formatCurrency(
    amounts: CurrencyPricingData,
    field: 'reward' | 'entryFee',
  ): CurrencyAmountDisplay {
    return this.themeService.formatCurrencyAmount(amounts.currency, amounts[field]);
  }
}
